import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getActingOrganizationId, getApiBase, getAuthHeaders } from '../../utils/api';

const FlightAwareLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedLogIndex, setExpandedLogIndex] = useState(null);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(50);
  const [logTypeFilter, setLogTypeFilter] = useState('');
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [loadingLogId, setLoadingLogId] = useState(null);

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [limit, logTypeFilter]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      setLoadError('');
      const params = new URLSearchParams({ limit: limit.toString() });
      if (logTypeFilter) {
        params.append('log_type', logTypeFilter);
      }
      const actingOrganizationId = getActingOrganizationId();
      if (actingOrganizationId) {
        params.append('organization_id', actingOrganizationId);
      }
      const response = await axios.get(`${getApiBase()}/flightaware-logs?${params.toString()}`, {
        headers: getAuthHeaders(),
      });
      if (response.data.success) {
        setLogs(response.data.logs);
        setTotal(response.data.total);
      }
    } catch (error) {
      console.error('Error fetching FlightAware logs:', error);
      setLoadError(error?.response?.data?.detail || error?.message || 'Unable to load FlightAware logs.');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      setStatsLoading(true);
      const actingOrganizationId = getActingOrganizationId();
      const response = await axios.get(`${getApiBase()}/flightaware-logs/stats`, {
        headers: getAuthHeaders(),
        params: actingOrganizationId ? { organization_id: actingOrganizationId } : undefined,
      });
      if (response.data.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching FlightAware stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleClearLogs = async () => {
    const actingOrganizationId = getActingOrganizationId();
    const scope = actingOrganizationId ? ` for organization ${actingOrganizationId}` : '';
    if (!window.confirm(`Are you sure you want to clear all FlightAware logs${scope}? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await axios.delete(`${getApiBase()}/flightaware-logs`, {
        headers: getAuthHeaders(),
        params: actingOrganizationId ? { organization_id: actingOrganizationId } : undefined,
      });
      if (response.data.success) {
        alert(`Successfully cleared ${response.data.deleted_count} logs`);
        fetchLogs();
        fetchStats();
      }
    } catch (error) {
      console.error('Error clearing logs:', error);
      alert('Error clearing logs. Please try again.');
    }
  };

  const toggleLogExpansion = async (index) => {
    if (expandedLogIndex === index) {
      setExpandedLogIndex(null);
      return;
    }

    const selectedLog = logs[index];
    if (!selectedLog?.id || selectedLog.request || selectedLog.response) {
      setExpandedLogIndex(index);
      return;
    }

    try {
      setLoadingLogId(selectedLog.id);
      setLoadError('');
      const actingOrganizationId = getActingOrganizationId();
      const response = await axios.get(`${getApiBase()}/flightaware-logs/${selectedLog.id}`, {
        headers: getAuthHeaders(),
        params: actingOrganizationId ? { organization_id: actingOrganizationId } : undefined,
      });
      if (response.data.success) {
        setLogs((currentLogs) => currentLogs.map((log, logIndex) => (
          logIndex === index ? response.data.log : log
        )));
        setExpandedLogIndex(index);
      }
    } catch (error) {
      console.error('Error fetching FlightAware log detail:', error);
      setLoadError(error?.response?.data?.detail || error?.message || 'Unable to load FlightAware log details.');
    } finally {
      setLoadingLogId(null);
    }
  };

  const getLogTypeConfig = (logType) => {
    switch (logType) {
      case 'api_call':
        return { label: 'API Call', color: 'bg-blue-100 text-blue-800', icon: '🔌' };
      case 'webhook_callback':
        return { label: 'Webhook', color: 'bg-purple-100 text-purple-800', icon: '📥' };
      case 'alert_registration':
        return { label: 'Alert Registration', color: 'bg-green-100 text-green-800', icon: '🔔' };
      case 'reconciliation':
        return { label: 'Reconciliation', color: 'bg-amber-100 text-amber-800', icon: '🔄' };
      default:
        return { label: logType || 'Unknown', color: 'bg-gray-100 text-gray-800', icon: '❓' };
    }
  };

  const getStatusColor = (success) => {
    return success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    // Older log entries appended Z to an existing +00:00 suffix. Normalize
    // those records so historical reconciliation logs remain readable.
    const normalized = String(timestamp).replace(/([+-]\d{2}:\d{2})Z$/, '$1');
    const date = new Date(normalized);
    return Number.isNaN(date.getTime()) ? String(timestamp) : date.toLocaleString();
  };

  // Format time preserving the original timezone (for departure/arrival times)
  // This displays the time as it appears in the original timezone, not converted to browser time
  const formatFlightTime = (isoString) => {
    if (!isoString) return 'N/A';

    try {
      // Parse the ISO string to extract components
      // Format: 2024-12-28T10:30:00-05:00 or 2024-12-28T10:30:00Z
      const match = isoString.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):?(\d{2})?([+-]\d{2}:\d{2}|Z)?/);
      if (!match) {
        // Fallback to basic parsing
        return new Date(isoString).toLocaleString();
      }

      const [, year, month, day, hours, minutes, seconds, tzOffset] = match;

      // Format date as MM/DD/YYYY
      const dateStr = `${parseInt(month)}/${parseInt(day)}/${year}`;

      // Format time as HH:MM AM/PM
      let hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      hour = hour % 12 || 12;
      const timeStr = `${hour}:${minutes} ${ampm}`;

      // Extract timezone abbreviation from offset
      let tzLabel = '';
      if (tzOffset) {
        if (tzOffset === 'Z') {
          tzLabel = 'UTC';
        } else {
          // Convert offset like -05:00 to a label
          const offsetMatch = tzOffset.match(/([+-])(\d{2}):(\d{2})/);
          if (offsetMatch) {
            const [, sign, offsetHours] = offsetMatch;
            const offsetNum = parseInt(offsetHours);
            // Common timezone labels based on offset (approximate - DST can shift these)
            const tzMap = {
              '-05:00': 'ET',   // Eastern Time (EST/EDT)
              '-04:00': 'AT',   // Atlantic Time
              '-06:00': 'CT',   // Central Time (CST/CDT)
              '-07:00': 'MT',   // Mountain Time (MST/MDT)
              '-08:00': 'PT',   // Pacific Time (PST/PDT)
              '-09:00': 'AKT',  // Alaska Time
              '-10:00': 'HT',   // Hawaii Time
              '+00:00': 'UTC',
              '+01:00': 'CET',  // Central European
              '+02:00': 'EET',  // Eastern European
              '+03:00': 'MSK',  // Moscow
              '+05:30': 'IST',  // India
              '+08:00': 'CST',  // China Standard
              '+09:00': 'JST',  // Japan
              '+10:00': 'AEST', // Australia Eastern
              '+11:00': 'AEDT', // Australia Eastern Daylight
              '-03:00': 'BRT',  // Brazil
            };
            tzLabel = tzMap[tzOffset] || `UTC${sign}${offsetNum}`;
          }
        }
      }

      return `${dateStr}, ${timeStr}${tzLabel ? ` ${tzLabel}` : ''}`;
    } catch (e) {
      // Fallback
      return isoString;
    }
  };

  const CopyButton = ({ text }) => {
    const [copied, setCopied] = useState(false);
    return (
      <button
        onClick={() => {
          navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
        className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 px-2 py-1 rounded hover:bg-blue-50"
        title="Copy to clipboard"
      >
        {copied ? '✓ Copied' : '📋 Copy'}
      </button>
    );
  };

  const renderPayload = (payload, title) => {
    if (!payload) return null;
    const jsonString = JSON.stringify(payload, null, 2);
    return (
      <div className="mt-2">
        <div className="flex items-center justify-between mb-1">
          <h5 className="font-semibold text-sm text-gray-700">{title}:</h5>
          <CopyButton text={jsonString} />
        </div>
        <pre className="bg-gray-50 p-3 rounded text-xs overflow-x-auto border border-gray-200 max-h-96">
          {jsonString}
        </pre>
      </div>
    );
  };

  const renderApiCallLog = (log, index) => {
    const request = log.request || {};
    const response = log.response || {};

    return (
      <div key={index} className="p-4 hover:bg-gray-50">
        <div
          className="cursor-pointer"
          onClick={() => toggleLogExpansion(index)}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                  🔌 API Call
                </span>
                <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                  {log.endpoint || 'Unknown Endpoint'}
                </span>
                <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(log.success)}`}>
                  {log.success ? 'SUCCESS' : 'FAILED'}
                </span>
                {loadingLogId === log.id && (
                  <span className="text-xs text-gray-500">Loading details…</span>
                )}
              </div>
              {/* Flight verification details */}
              {log.endpoint === 'verify_flight' && (
                <div className="text-sm text-gray-900 mb-1">
                  <span className="font-medium">Flight:</span>{' '}
                  <span className="text-gray-700">
                    {request.airline_code}{request.flight_number}
                  </span>
                  {request.origin && (
                    <span className="text-gray-500 ml-2">
                      {request.origin} → {request.destination || '?'}
                    </span>
                  )}
                  {(request.departure_date || request.arrival_date) && (
                    <span className="text-gray-500 ml-2">
                      ({request.departure_date || request.arrival_date})
                    </span>
                  )}
                </div>
              )}
              {/* Response summary */}
              {response.total_legs !== undefined && (
                <div className="text-sm text-gray-700 mb-1">
                  <span className="font-medium">Result:</span>{' '}
                  {response.total_legs} leg(s) found
                </div>
              )}
              {log.error && (
                <div className="text-sm text-red-600 mb-1">
                  <span className="font-medium">Error:</span> {log.error}
                </div>
              )}
              <div className="text-xs text-gray-500">
                {formatTimestamp(log.timestamp || log.created_at)}
              </div>
            </div>
            <div className="ml-4 flex-shrink-0">
              <svg
                className={`h-5 w-5 text-gray-400 transform transition-transform ${
                  expandedLogIndex === index ? 'rotate-180' : ''
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>

        {expandedLogIndex === index && (
          <div className="mt-4 border-t border-gray-200 pt-4">
            {log.error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
                <h5 className="font-semibold text-sm text-red-800 mb-1">Error:</h5>
                <p className="text-sm text-red-700">{log.error}</p>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {renderPayload(request, 'Request')}
              {renderPayload(response, 'Response')}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderWebhookLog = (log, index) => {
    const request = log.request || {};
    const response = log.response || {};

    return (
      <div key={index} className="p-4 hover:bg-gray-50">
        <div
          className="cursor-pointer"
          onClick={() => toggleLogExpansion(index)}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-800">
                  📥 Webhook Callback
                </span>
                <span className="px-2 py-1 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                  {request.event_type || 'unknown'}
                </span>
                <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(log.success)}`}>
                  {log.success ? 'PROCESSED' : 'FAILED'}
                </span>
              </div>
              {/* Webhook details */}
              <div className="text-sm text-gray-900 mb-1">
                <span className="font-medium">Flight ID:</span>{' '}
                <span className="font-mono text-blue-600">
                  {request.fa_flight_id || log.fa_flight_id || 'N/A'}
                </span>
              </div>
              {/* Status update info */}
              {request.flight_status && (
                <div className="text-sm text-gray-700 mb-1">
                  <span className="font-medium">Status:</span>{' '}
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                    request.flight_status === 'landed' || request.flight_status === 'arrived'
                      ? 'bg-green-100 text-green-800'
                      : request.flight_status === 'cancelled'
                        ? 'bg-red-100 text-red-800'
                        : request.flight_status === 'active' || request.flight_status === 'en_route'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                  }`}>
                    {request.flight_status}
                  </span>
                </div>
              )}
              {/* Gate info */}
              {(request.departure_gate || request.arrival_gate) && (
                <div className="text-sm text-gray-700 mb-1">
                  {request.departure_gate && (
                    <span className="mr-3">
                      <span className="font-medium">Dep Gate:</span> {request.departure_gate}
                    </span>
                  )}
                  {request.arrival_gate && (
                    <span>
                      <span className="font-medium">Arr Gate:</span> {request.arrival_gate}
                    </span>
                  )}
                </div>
              )}
              {/* Time updates - shown in local airport timezone */}
              {(request.estimated_departure || request.estimated_arrival || request.actual_departure || request.actual_arrival) && (
                <div className="text-sm text-gray-700 mb-1">
                  {(request.actual_departure || request.estimated_departure) && (
                    <span className="mr-3">
                      <span className="font-medium">Dep:</span>{' '}
                      {request.actual_departure ? 'Actual' : 'Est'}{' '}
                      {formatFlightTime(request.actual_departure || request.estimated_departure)}
                    </span>
                  )}
                  {(request.actual_arrival || request.estimated_arrival) && (
                    <span>
                      <span className="font-medium">Arr:</span>{' '}
                      {request.actual_arrival ? 'Actual' : 'Est'}{' '}
                      {formatFlightTime(request.actual_arrival || request.estimated_arrival)}
                    </span>
                  )}
                </div>
              )}
              {log.error && (
                <div className="text-sm text-red-600 mb-1">
                  <span className="font-medium">Error:</span> {log.error}
                </div>
              )}
              <div className="text-xs text-gray-500">
                {formatTimestamp(log.timestamp || log.created_at)}
              </div>
            </div>
            <div className="ml-4 flex-shrink-0">
              <svg
                className={`h-5 w-5 text-gray-400 transform transition-transform ${
                  expandedLogIndex === index ? 'rotate-180' : ''
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>

        {expandedLogIndex === index && (
          <div className="mt-4 border-t border-gray-200 pt-4">
            {log.error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
                <h5 className="font-semibold text-sm text-red-800 mb-1">Error:</h5>
                <p className="text-sm text-red-700">{log.error}</p>
              </div>
            )}

            {/* Extra info */}
            {(log.flight_id || log.trip_id || log.leg_number !== undefined) && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
                <h5 className="font-semibold text-sm text-blue-800 mb-2">Associated Records:</h5>
                <div className="text-sm space-y-1">
                  {log.flight_id && (
                    <div><span className="font-medium">Flight ID:</span> {log.flight_id}</div>
                  )}
                  {log.trip_id && (
                    <div><span className="font-medium">Trip ID:</span> {log.trip_id}</div>
                  )}
                  {log.leg_number !== undefined && (
                    <div><span className="font-medium">Leg:</span> {log.leg_number}</div>
                  )}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {renderPayload(request, 'Webhook Payload')}
              {renderPayload(response, 'Processing Result')}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderAlertRegistrationLog = (log, index) => {
    const request = log.request || {};
    const response = log.response || {};

    return (
      <div key={index} className="p-4 hover:bg-gray-50">
        <div
          className="cursor-pointer"
          onClick={() => toggleLogExpansion(index)}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                  🔔 Alert Registration
                </span>
                <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(log.success)}`}>
                  {log.success ? 'REGISTERED' : 'FAILED'}
                </span>
              </div>
              {/* Flight info */}
              <div className="text-sm text-gray-900 mb-1">
                <span className="font-medium">Flight:</span>{' '}
                <span className="text-gray-700">
                  {request.airline_code}{request.flight_number}
                </span>
                {request.origin && (
                  <span className="text-gray-500 ml-2">
                    {request.origin} → {request.destination || '?'}
                  </span>
                )}
                {request.departure_date && (
                  <span className="text-gray-500 ml-2">
                    ({request.departure_date})
                  </span>
                )}
              </div>
              {/* Alert ID */}
              {response.alert_id && (
                <div className="text-sm text-gray-700 mb-1">
                  <span className="font-medium">Alert ID:</span>{' '}
                  <span className="font-mono text-xs">{response.alert_id}</span>
                </div>
              )}
              {/* Events subscribed */}
              {request.events && (
                <div className="text-sm text-gray-700 mb-1">
                  <span className="font-medium">Events:</span>{' '}
                  {Array.isArray(request.events)
                    ? request.events.join(', ')
                    : Object.entries(request.events).filter(([k, v]) => v === true).map(([k]) => k).join(', ')
                  }
                </div>
              )}
              {log.error && (
                <div className="text-sm text-red-600 mb-1">
                  <span className="font-medium">Error:</span> {log.error}
                </div>
              )}
              <div className="text-xs text-gray-500">
                {formatTimestamp(log.timestamp || log.created_at)}
              </div>
            </div>
            <div className="ml-4 flex-shrink-0">
              <svg
                className={`h-5 w-5 text-gray-400 transform transition-transform ${
                  expandedLogIndex === index ? 'rotate-180' : ''
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>

        {expandedLogIndex === index && (
          <div className="mt-4 border-t border-gray-200 pt-4">
            {log.error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
                <h5 className="font-semibold text-sm text-red-800 mb-1">Error:</h5>
                <p className="text-sm text-red-700">{log.error}</p>
              </div>
            )}

            {/* Extra info */}
            {(log.flight_id || log.trip_id || log.fa_flight_id) && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded">
                <h5 className="font-semibold text-sm text-green-800 mb-2">Associated Records:</h5>
                <div className="text-sm space-y-1">
                  {log.fa_flight_id && (
                    <div><span className="font-medium">FA Flight ID:</span> <span className="font-mono text-xs">{log.fa_flight_id}</span></div>
                  )}
                  {log.flight_id && (
                    <div><span className="font-medium">Flight ID:</span> {log.flight_id}</div>
                  )}
                  {log.trip_id && (
                    <div><span className="font-medium">Trip ID:</span> {log.trip_id}</div>
                  )}
                  {log.leg_number !== undefined && (
                    <div><span className="font-medium">Leg:</span> {log.leg_number}</div>
                  )}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {renderPayload(request, 'Registration Request')}
              {renderPayload(response, 'Registration Response')}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderLogEntry = (log, index) => {
    if (log.log_type === 'webhook_callback') {
      return renderWebhookLog(log, index);
    } else if (log.log_type === 'alert_registration') {
      return renderAlertRegistrationLog(log, index);
    } else {
      return renderApiCallLog(log, index);
    }
  };

  if (loading && logs.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">FlightAware API Logs</h1>
          <p className="mt-1 text-sm text-gray-500">
            View FlightAware API calls, webhook callbacks, and alert registrations
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <select
            value={logTypeFilter}
            onChange={(e) => setLogTypeFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Log Types</option>
            <option value="api_call">🔌 API Calls</option>
            <option value="webhook_callback">📥 Webhook Callbacks</option>
            <option value="alert_registration">🔔 Alert Registration</option>
            <option value="reconciliation">🔄 Reconciliation</option>
          </select>
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value={25}>Show 25</option>
            <option value={50}>Show 50</option>
            <option value={100}>Show 100</option>
            <option value={200}>Show 200</option>
          </select>
          <button
            onClick={() => { fetchLogs(); fetchStats(); }}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Refresh
          </button>
          <button
            onClick={handleClearLogs}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          >
            Clear All
          </button>
        </div>
      </div>

      {loadError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <div className="font-semibold">FlightAware logs could not be loaded</div>
          <div className="mt-1">{loadError}</div>
        </div>
      )}

      {/* Stats */}
      {stats && !statsLoading && (
        <div className="bg-white shadow rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">FlightAware Usage Statistics</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-gray-900">{stats.total_requests}</div>
              <div className="text-sm text-gray-500">Total Logs</div>
            </div>
            {stats.by_log_type?.api_call && (
              <div className="bg-blue-50 rounded-lg p-3">
                <div className="text-2xl font-bold text-blue-900">
                  {stats.by_log_type.api_call.total}
                  <span className="text-sm font-normal text-blue-600 ml-2">
                    ({stats.by_log_type.api_call.success} ok / {stats.by_log_type.api_call.errors} err)
                  </span>
                </div>
                <div className="text-sm text-blue-700">🔌 API Calls</div>
              </div>
            )}
            {stats.by_log_type?.webhook_callback && (
              <div className="bg-purple-50 rounded-lg p-3">
                <div className="text-2xl font-bold text-purple-900">
                  {stats.by_log_type.webhook_callback.total}
                  <span className="text-sm font-normal text-purple-600 ml-2">
                    ({stats.by_log_type.webhook_callback.success} ok / {stats.by_log_type.webhook_callback.errors} err)
                  </span>
                </div>
                <div className="text-sm text-purple-700">📥 Webhook Callbacks</div>
              </div>
            )}
            {stats.by_log_type?.alert_registration && (
              <div className="bg-green-50 rounded-lg p-3">
                <div className="text-2xl font-bold text-green-900">
                  {stats.by_log_type.alert_registration.total}
                  <span className="text-sm font-normal text-green-600 ml-2">
                    ({stats.by_log_type.alert_registration.success} ok / {stats.by_log_type.alert_registration.errors} err)
                  </span>
                </div>
                <div className="text-sm text-green-700">🔔 Alert Registrations</div>
              </div>
            )}
            {stats.by_log_type?.reconciliation && (
              <div className="bg-amber-50 rounded-lg p-3">
                <div className="text-2xl font-bold text-amber-900">
                  {stats.by_log_type.reconciliation.total}
                  <span className="text-sm font-normal text-amber-600 ml-2">
                    ({stats.by_log_type.reconciliation.success} ok / {stats.by_log_type.reconciliation.errors} err)
                  </span>
                </div>
                <div className="text-sm text-amber-700">🔄 Reconciliations</div>
              </div>
            )}
          </div>
          {stats.last_24_hours && Object.keys(stats.last_24_hours).length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <div className="text-xs text-gray-500">
                Last 24 hours:{' '}
                {Object.entries(stats.last_24_hours).map(([type, count], idx) => (
                  <span key={type}>
                    {idx > 0 && ', '}
                    {type === 'api_call' ? '🔌' : type === 'webhook_callback' ? '📥' : type === 'reconciliation' ? '🔄' : '🔔'} {count}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Log Count */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="text-sm text-gray-600">
          Showing <span className="font-semibold">{logs.length}</span> of{' '}
          <span className="font-semibold">{total}</span> total logs
          {logTypeFilter && (
            <span className="ml-2 text-gray-400">
              (filtered by {logTypeFilter === 'api_call' ? '🔌 API Calls' : logTypeFilter === 'webhook_callback' ? '📥 Webhook Callbacks' : logTypeFilter === 'reconciliation' ? '🔄 Reconciliation' : '🔔 Alert Registration'})
            </span>
          )}
        </div>
      </div>

      {/* Logs List */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {logs.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p className="text-lg">No FlightAware logs yet</p>
            <p className="text-sm mt-2">
              {logTypeFilter === 'webhook_callback'
                ? 'Webhook callbacks will appear here when FlightAware sends flight updates'
                : logTypeFilter === 'alert_registration'
                  ? 'Alert registrations will appear here when subscribing to flight tracking'
                  : logTypeFilter === 'reconciliation'
                    ? 'Reconciliation runs will appear here after a superadmin preview or apply run'
                  : logTypeFilter === 'api_call'
                    ? 'API calls will appear here when verifying flights'
                    : 'FlightAware activity will appear here when using flight tracking features'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {logs.map((log, index) => renderLogEntry(log, index))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FlightAwareLogs;
